import { Fragment, type ReactNode } from "react";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

const SKILL_TAG_COLORS: Record<string, string> = {
  magicDamage: "#31c8e6",
  physicalDamage: "#ff9b6b",
  trueDamage: "#e0c56e",
  scaleAP: "#31c8e6",
  scaleAD: "#ff9b6b",
  scaleHealth: "#8fce5b",
  scaleMana: "#5db6ff",
  scaleArmor: "#f0b072",
  scaleMR: "#8fd6ff",
  healing: "#4bd15a",
  lifeSteal: "#4bd15a",
  shield: "#c9c9d6",
  speed: "#e0a458",
  status: "#ffffff",
  keyword: "#f0e6d2",
  keywordMajor: "#f0e6d2",
  spellName: "#d5a353",
  armorPen: "#ff9b6b",
  magicPen: "#31c8e6",
  attackSpeed: "#e0a458",
  scaleCrit: "#ff9b6b",
  rules: "#8b8ba7",
  flavorText: "#8b8ba7",
};

// <font color='#RRGGBB'> is the ONLY tag in the classic champion/item data that
// carries an attribute — verified across all 60 champions and all 150 items on
// 16.15. It is also the only one the old name-only tokenizer could not match:
// it skipped the opening tag (which then rendered as literal
// "<font color='#FF9900'>" text in the tooltip) while still matching the
// attribute-less </font>, popping a color that had never been pushed.
//
// 13 uses, all balanced, across six champions: Olaf, Twisted Fate, Sion,
// Miss Fortune, Tryndamere and Dr. Mundo.
const FONT_TAG_COLOR = /\bcolor\s*=\s*(?:'([^']*)'|"([^"]*)"|([^\s'">]+))/i;

// Only #RGB, #RRGGBB or a bare CSS keyword may reach the style prop.
const SAFE_COLOR = /^(?:#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})|[a-zA-Z]+)$/;

// Riot picks these against the light League client background, and two of them
// are unreadable on our dark tooltip: pure blue (Twisted Fate's "Blue Card")
// lands around 2:1 contrast and #CC3300 (Sion, Dr. Mundo) around 3.3:1. Remap
// those two deliberately instead of inheriting them by accident; #FF9900,
// #FFD700, #FF0000 and #FF3300 are legible and pass through untouched.
const FONT_COLOR_OVERRIDES: Record<string, string> = {
  "#0000ff": "#5db6ff",
  "#cc3300": "#e0524f",
};

function fontTagColor(attrs: string): string | undefined {
  const match = attrs.match(FONT_TAG_COLOR);
  const raw = (match ? match[1] ?? match[2] ?? match[3] ?? "" : "").trim();
  if (!raw || !SAFE_COLOR.test(raw)) return undefined;
  return FONT_COLOR_OVERRIDES[raw.toLowerCase()] || raw;
}

// Parse a dynamicDescription into React nodes: resolve the color tags, turn
// <br> into line breaks, drop the @SpellModifierDescriptionAppend@ hook, and
// replace every other @Placeholder@ token with a "?" (inheriting the current
// tag color). `hasMissing` is true when any "?" is emitted or already present,
// which drives the "? indicates missing/incorrect data" footnote.
function renderDynamicDescription(text: string | undefined): { node: ReactNode; hasMissing: boolean } {
  if (typeof text !== "string" || !text) return { node: null, hasMissing: false };

  let hasMissing = false;
  // The `(?:\s[^<>]*?)?` arm is what lets an attributed tag through.
  const tokens = text.split(/(<br\s*\/?>|<\/?[a-zA-Z][a-zA-Z0-9]*(?:\s[^<>]*?)?\/?>|@[^@]+@)/g);
  const colorStack: Array<string | undefined> = [];
  const nodes: ReactNode[] = [];
  let key = 0;

  for (const tok of tokens) {
    if (!tok) continue;

    if (/^<br\s*\/?>$/.test(tok)) {
      nodes.push(<br key={key++} />);
      continue;
    }
    const open = tok.match(/^<([a-zA-Z][a-zA-Z0-9]*)((?:\s[^<>]*?)?)(\/?)>$/);
    if (open) {
      const [, name, attrs, selfClosing] = open;
      // A self-closing tag opens nothing, so it must not push onto the stack.
      if (selfClosing) continue;
      colorStack.push(name.toLowerCase() === "font" ? fontTagColor(attrs) : SKILL_TAG_COLORS[name]);
      continue;
    }
    const close = tok.match(/^<\/([a-zA-Z][a-zA-Z0-9]*)\s*>$/);
    if (close) {
      colorStack.pop();
      continue;
    }
    const at = tok.match(/^@([^@]+)@$/);
    if (at) {
      // Text-append hook, not a value — render nothing.
      if (at[1] === "SpellModifierDescriptionAppend") continue;
      hasMissing = true;
      const color = colorStack[colorStack.length - 1];
      nodes.push(
        <span key={key++} style={color ? { color } : undefined}>
          ?
        </span>,
      );
      continue;
    }

    if (tok.includes("?")) hasMissing = true;
    const color = colorStack[colorStack.length - 1];
    nodes.push(
      color ? (
        <span key={key++} style={{ color }}>
          {tok}
        </span>
      ) : (
        <Fragment key={key++}>{tok}</Fragment>
      ),
    );
  }

  return { node: <>{nodes}</>, hasMissing };
}

// Collapse a per-rank cooldown array to a display string: a single value when
// every rank shares it, otherwise the "a/b/c" ladder.
export function formatCooldown(arr: number[] | undefined): string | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return new Set(arr).size === 1 ? String(arr[0]) : arr.join("/");
}

// Shared tooltip body used both for hover tooltips (Skills Overview + header
// skill icons) and inline in the Skill Tooltips debug module.
export function SkillTooltipContent(props: {
  name: string;
  cooldown?: string | null;
  description: string | undefined;
  isPassive?: boolean;
}) {
  const { name, cooldown, description, isPassive } = props;
  const { node, hasMissing } = renderDynamicDescription(description);

  return (
    <div className="max-w-[320px] text-[13px] leading-[18px]">
      <div className="font-bold text-accent-blue-400">{name}</div>
      {!isPassive && cooldown && <div className="mt-[2px] text-[12px] text-lavender-50">Cooldown: {cooldown}</div>}
      <div className="mt-[6px] text-white">{node}</div>
      {hasMissing && (
        <div className="mt-[8px] text-[11px] text-[#ffa514]">"?" indicates missing or incorrect data from Riot API</div>
      )}
    </div>
  );
}

// Role query-param default (drives labels + matchup links). Role option chips
// reuse the shared ROLE_OPTIONS (with the site's role icons).

// Ability-key display labels, shared by the Recommended panel skill-priority
// row and the standalone Skills module.
export const SKILL_KEY_LABEL: Record<string, string> = { q: "Q", w: "W", e: "E", r: "R", p: "P" };
